import React, { useState, useRef, useEffect, useMemo } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal.js";
import AlertNotification from "../../components/AlertNotification";
import apiHelper from "../../apiHelper.js";
import noImage from "../../assets/img/brand/noImage.png";
import emptyBoxImg from "../../assets/img/brand/no-brand-img.png";

const BULK_TYPES = ["Bulk Input", "Use Base Price", "Use Pricing Template"];

const BULK_TYPE_DISPLAY = {
  "Bulk Input": "Bulk Input",
  "Use Base Price": "Use Base Price",
  "Use Pricing Template": "Use Pricing Template",
};

function formatSGD(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(String(value).replace(/[^\d.-]/g, ""));
  if (isNaN(num)) return "";
  return num.toFixed(2);
}

// Only allow digits with an optional decimal point, matching the Order filter's price fields
const isValidPriceInput = (val) => val === "" || /^\d*\.?\d*$/.test(val);

// Round a typed price to 2 decimal places on blur, same as the Order filter's price fields
const roundPriceOnBlur = (val) => {
  if (val === "" || val === undefined || val === null) return val;
  const num = parseFloat(val);
  return isNaN(num) ? val : num.toFixed(2);
};

export default function BulkPricing() {
  const history = useHistory();
  const location = useLocation();
  const breadcrumbs = ["Pricing", "Platform Pricing", "Bulk Pricing"];

  const incomingItems = location?.state?.bulkItems ?? [];
  const [selectedItems, setSelectedItems] = useState(incomingItems);

  // Edited prices: key = `${listingId}_base` or `${listingId}_${mappingId}`
  const [editedPrices, setEditedPrices] = useState(() => {
    const init = {};
    incomingItems.forEach((item) => {
      init[`${item.id}_base`] = item.basePrice != null ? Number(item.basePrice).toFixed(2) : "";
      (item.platforms || []).forEach((p) => {
        const raw = p.override_price_raw;
        init[`${item.id}_${p.sales_listing_mapping_id}`] = raw != null ? formatSGD(raw) : "";
      });
    });
    return init;
  });

  const [priceErrors, setPriceErrors] = useState({});
  const [saveError, setSaveError] = useState(false);

  // Discard modal
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [discardSource, setDiscardSource] = useState(null);
  const [nextLocation, setNextLocation] = useState(null);
  const allowNavigationRef = useRef(false);


  // Bulk Edit panel
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkBasePrice, setBulkBasePrice] = useState("");
  // per-platform bulk config: { [platformName]: { type, value } }
  const allPlatforms = useMemo(() => {
    const seen = new Set();
    const result = [];
    selectedItems.forEach((item) =>
      (item.platforms || []).forEach((p) => {
        if (!seen.has(p.name)) { seen.add(p.name); result.push(p.name); }
      })
    );
    return result;
  }, [selectedItems]);
  const [bulkPlatformTypes, setBulkPlatformTypes] = useState({});
  const [bulkPlatformValues, setBulkPlatformValues] = useState({});
  // bulkPlatformTemplates: { [platformName]: { id, name, fees } }
  const [bulkPlatformTemplates, setBulkPlatformTemplates] = useState({});
  const [bulkErrors, setBulkErrors] = useState({});
  const [openBulkTypeDropdown, setOpenBulkTypeDropdown] = useState(null);
  const [openTemplateDropdown, setOpenTemplateDropdown] = useState(null);
  const bulkTypeDropdownRef = useRef({});
  const templateDropdownRef = useRef({});
  const [templateSearch, setTemplateSearch] = useState({});
  const bulkAppliedRef = useRef(false);

  // Pricing templates
  const [pricingTemplates, setPricingTemplates] = useState([]);
  useEffect(() => {
    apiHelper("sales/pricing-templates/?status=active&page_size=0", "GET")
      .then((r) => r.ok ? r.json() : { results: [] })
      .then((data) => setPricingTemplates(data.results || []))
      .catch(() => {});
  }, []);

  // Notification
  const [notification, setNotification] = useState(null);

  // Unsaved changes: prices edited, a row removed, or bulk edit data entered
  const initialPricesRef = useRef(editedPrices);
  const isDirty =
    selectedItems.length !== incomingItems.length ||
    Object.keys(editedPrices).some((k) => (editedPrices[k] ?? "") !== (initialPricesRef.current[k] ?? "")) ||
    bulkBasePrice.trim() !== "" ||
    Object.values(bulkPlatformTypes).some(Boolean);

  // Browser refresh / tab close guard — shows the native "Leave site?" confirmation
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Navigation block
  useEffect(() => {
    const unblock = history.block((tx) => {
      if (allowNavigationRef.current) { allowNavigationRef.current = false; return true; }
      setDiscardSource("navigate");
      setNextLocation(tx);
      setDiscardModalOpen(true);
      return false;
    });
    return () => unblock();
  }, []);

  // Close bulk type dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        openBulkTypeDropdown !== null &&
        bulkTypeDropdownRef.current[openBulkTypeDropdown] &&
        !bulkTypeDropdownRef.current[openBulkTypeDropdown].contains(e.target)
      ) {
        setOpenBulkTypeDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openBulkTypeDropdown]);

  // Close template dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        openTemplateDropdown !== null &&
        templateDropdownRef.current[openTemplateDropdown] &&
        !templateDropdownRef.current[openTemplateDropdown].contains(e.target)
      ) {
        setOpenTemplateDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openTemplateDropdown]);


  const handleDiscard = () => {
    setDiscardSource("cancel");
    setNextLocation(null);
    setDiscardModalOpen(true);
  };

  const handleDiscardConfirm = () => {
    allowNavigationRef.current = true;
    setDiscardModalOpen(false);
    if (discardSource === "cancel") {
      history.push("/platform-pricing");
      return;
    }
    if (discardSource === "navigate" && nextLocation) {
      history.push(nextLocation.pathname, nextLocation.state || {});
      return;
    }
    history.push("/platform-pricing");
  };

  const handleDeleteRow = (itemId) => {
    setSelectedItems((prev) => prev.filter((i) => i.id !== itemId));
    setPriceErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${itemId}_`)) delete next[k]; });
      return next;
    });
    setEditedPrices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${itemId}_`)) delete next[k]; });
      return next;
    });
  };

  function calcTemplatePrice(baseStr, template) {
    const base = parseFloat(String(baseStr).replace(/,/g, ""));
    if (isNaN(base) || !template?.fees?.length) return baseStr;
    let price = base;
    (template.fees || []).forEach((fee) => {
      // fee_type: 1 = Flat Amount, 2 = Percentage of Base Price
      const isPercentage = Number(fee.fee_type) === 2;
      if (isPercentage) {
        let addition = (base * parseFloat(fee.fee_value || 0)) / 100;
        const cap = parseFloat(fee.cap_amount);
        if (!isNaN(cap) && cap > 0) addition = Math.min(addition, cap);
        price += addition;
      } else {
        price += parseFloat(fee.fee_value || 0);
      }
    });
    return Math.max(0, price).toFixed(2);
  }

  const clearBulkType = (pname) => {
    setBulkPlatformTypes((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkPlatformValues((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkPlatformTemplates((prev) => { const n = { ...prev }; delete n[pname]; return n; });
    setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; return n; });
  };

  // Apply bulk edit values
  const handleBulkApply = () => {
    const errors = {};

    const bulkBase = bulkBasePrice.trim();
    // A base price of 0 counts as empty, same as the Platform Pricing table
    const isEmptyPrice = (val) => {
      const num = parseFloat(String(val ?? "").replace(/,/g, ""));
      return isNaN(num) || num <= 0;
    };

    allPlatforms.forEach((pname) => {
      const btype = bulkPlatformTypes[pname];
      if (!btype) return;

      // Base Price is only required for types whose platform price is derived
      // from it ("Use Base Price" / "Use Pricing Template"). "Bulk Input" sets
      // the platform price directly, so it doesn't need a Base Price.
      const needsBasePrice = btype === "Use Base Price" || btype === "Use Pricing Template";
      const anyBasePriceMissing = needsBasePrice && selectedItems.some((item) => {
        const hasPlatform = (item.platforms || []).some((p) => p.name === pname);
        if (!hasPlatform) return false;
        const effectiveBase = bulkBase !== "" ? bulkBase : editedPrices[`${item.id}_base`];
        return isEmptyPrice(effectiveBase);
      });
      if (anyBasePriceMissing) {
        errors[pname] = "Please complete all Base Price first";  // type column
      }

      if (btype === "Use Pricing Template" && !bulkPlatformTemplates[pname]) {
        errors[`${pname}_value`] = "Please select a Pricing Template";  // value column
      } else if (btype === "Bulk Input") {
        const val = (bulkPlatformValues[pname] || "").trim();
        if (!val) {
          errors[`${pname}_value`] = "Value is required";  // value column
        } else if (isNaN(Number(val.replace(/,/g, "")))) {
          errors[`${pname}_value`] = "Invalid Price";  // value column
        }
      }
    });

    setBulkErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const updates = {};
    selectedItems.forEach((item) => {
      if (bulkBasePrice.trim()) {
        updates[`${item.id}_base`] = bulkBasePrice.trim();
      }
      (item.platforms || []).forEach((p) => {
        const btype = bulkPlatformTypes[p.name];
        if (!btype) return;
        const key = `${item.id}_${p.sales_listing_mapping_id}`;
        const baseVal = updates[`${item.id}_base`] ?? editedPrices[`${item.id}_base`] ?? "";
        if (btype === "Bulk Input") {
          updates[key] = bulkPlatformValues[p.name] || "";
        } else if (btype === "Use Base Price") {
          updates[key] = baseVal;
        } else if (btype === "Use Pricing Template") {
          const tmpl = bulkPlatformTemplates[p.name];
          updates[key] = baseVal ? calcTemplatePrice(baseVal, tmpl) : "";
        }
      });
    });
    setEditedPrices((prev) => ({ ...prev, ...updates }));
    bulkAppliedRef.current = true;
  };

  const handleSaveClick = async () => {
    const errors = {};
    let hasError = false;
    selectedItems.forEach((item) => {
      // Validate platform prices
      (item.platforms || []).forEach((p) => {
        const key = `${item.id}_${p.sales_listing_mapping_id}`;
        const val = editedPrices[key];
        if (!val || val.trim() === "") {
          errors[key] = "Platform Price is required";
          hasError = true;
        } else if (isNaN(Number(String(val).replace(/,/g, "")))) {
          errors[key] = "Invalid Price";
          hasError = true;
        }
      });
    });
    setPriceErrors(errors);
    if (hasError) {
      setSaveError(true);
      setNotification({ type: "error", message: "Error Saving" });
      return;
    }
    setSaveError(false);
    setNotification(null);

    const listingsPayload = selectedItems.map((item) => ({
      listing_id: item.id,
      prices: (item.platforms || []).map((p) => {
        const baseKey = `${item.id}_base`;
        const priceKey = `${item.id}_${p.sales_listing_mapping_id}`;
        const baseVal = editedPrices[baseKey] || "";
        const overrideVal = editedPrices[priceKey] || "";
        const parsedBase = parseFloat(String(baseVal).replace(/[^\d.]/g, ""));
        const parsedOverride = parseFloat(String(overrideVal).replace(/,/g, ""));
        return {
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          sales_platform_id: p.id,
          base_price: !isNaN(parsedBase) ? parsedBase : (item.basePrice != null ? Number(item.basePrice) : 0),
          override_price: !isNaN(parsedOverride) ? parsedOverride : 0,
        };
      }),
    }));

    try {
      const res = await apiHelper("sales/pricing/bulk-pricing/", "POST", {
        reason: "Price update",
        listings: listingsPayload,
      });
      if (res.ok) {
        allowNavigationRef.current = true;
        history.push("/platform-pricing", {
          notification: { type: "success", message: "Pricing Saved Successfully" },
        });
      } else {
        const err = await res.json().catch(() => ({}));
        console.error("Save failed", err);
        setNotification({ type: "error", message: "Failed to save pricing" });
      }
    } catch (e) {
      console.error(e);
      setNotification({ type: "error", message: "Network error" });
    }
  };

  const isEmpty = selectedItems.length === 0;

  const inputWrapStyle = (hasError) => ({
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${hasError ? "#DC3545" : "#6C757D"}`,
    borderRadius: 8,
    height: 40,
    overflow: "hidden",
    background: "#fff",
    width: "100%",
    maxWidth: 220,
  });

  const sgdStyle = (hasError) => ({
    padding: "0 10px",
    fontSize: 14,
    color: hasError ? "#DC3545" : "#6C757D",
    borderRight: `1px solid ${hasError ? "#DC3545" : "#6C757D"}`,
    height: "100%",
    display: "flex",
    alignItems: "center",
    background: "#F8F9FA",
    whiteSpace: "nowrap",
  });

  const inputStyle = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    padding: "0 10px",
    fontSize: 14,
    height: "100%",
    flex: 1,
    minWidth: 0,
    color: "#212529",
    fontFamily: "Montserrat",
  };

  return (
    <div className="company-body main-company-body-inner new-look bulk-pricing-root">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          <span
            key={i}
            className={`breadcrumb-item${crumb === "Platform Pricing" ? " clickable" : ""}`}
            style={{ cursor: crumb === "Platform Pricing" ? "pointer" : "default" }}
            onClick={() => crumb === "Platform Pricing" && history.push("/platform-pricing")}
          >
            {crumb}{i < breadcrumbs.length - 1 ? "  " : ""}
          </span>
        ))}
      </div>

      {/* Header */}
      <div className="edit-organisation-info">
        <div className="edit-organisation-left">
          <span style={{ color: "#212529", fontFamily: "Montserrat", fontSize: 20, fontWeight: 700 }}>
            Bulk Pricing
          </span>
        </div>
        <div className="discard-save-organisation-right d-flex align-items-center justify-content-end gap-2">
          {saveError && notification && (
            <AlertNotification
              type={notification.type}
              message={notification.message}
              onClose={() => { setNotification(null); setSaveError(false); }}
            />
          )}
          <button
            type="button"
            onClick={handleDiscard}
            style={{
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              borderRadius: 8,
              border: "1px solid #212529",
              background: "#fff",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#212529",
              cursor: "pointer",
            }}
          >
            Discard
          </button>
          <button
            type="button"
            onClick={!isEmpty ? handleSaveClick : undefined}
            style={isEmpty ? {
              borderRadius: 8,
              border: "1px solid #CED4DA",
              background: "#E9ECEF",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              cursor: "not-allowed",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#6C757D",
            } : {
              borderRadius: 8,
              border: "1px solid #212529",
              background: "#212529",
              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
              height: 34,
              minWidth: 100,
              padding: "0 10px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "Montserrat",
              color: "#fff",
            }}
          >
            Save
          </button>
        </div>
      </div>

      <DiscardConfirmationModal
        isOpen={discardModalOpen}
        onCancel={() => { setDiscardModalOpen(false); setNextLocation(null); setDiscardSource(null); }}
        onConfirm={handleDiscardConfirm}
        entityName="Bulk Pricing"
      />


      <div className="employee-tabs-container">
        <div className="tab-content" id="bulk-pricing-v2-content">
          <div className="info-section">
            <div className="info-card new-info-overflow-auto main-company-body-inner" style={{ borderRadius: 12, boxShadow: "0px 1px 2px rgba(0,0,0,0.1)" }}>
              <div className="info-content d-block entities-tab">

                <>
                  {/* Bulk Edit Button + Panel */}
                  {!isEmpty && <div style={{ background: "#F8F9FA", borderRadius: 8, padding: 10, marginBottom: 20 }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (bulkEditOpen && !bulkAppliedRef.current) {
                          setBulkBasePrice("");
                          setBulkPlatformTypes({});
                          setBulkPlatformValues({});
                          setBulkPlatformTemplates({});
                          setBulkErrors({});
                          setTemplateSearch({});
                        }
                        if (bulkEditOpen) bulkAppliedRef.current = false;
                        setBulkEditOpen((o) => !o);
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "10px", borderRadius: 8, border: "none",
                        backgroundColor: "#212529", color: "#fff",
                        fontFamily: "Montserrat", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      Bulk Edit
                      {bulkEditOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {bulkEditOpen && (
                        <div style={{ marginTop: 10, borderRadius: 10, background: "#F8F9FA", padding: "16px" }}>
                          {/* Bulk Base Price */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 11, flexWrap: "nowrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap" }}>
                                Bulk Edit Base Price
                              </span>
                              <span style={{ fontSize: 10, color: "#6C757D", fontFamily: "Montserrat", fontWeight: 400, whiteSpace: "nowrap" }}>
                                (Applied to all Base Price values below)
                              </span>
                            </div>
                            <div style={{ ...inputWrapStyle(false), maxWidth: 525 }}>
                              <div style={sgdStyle(false)}>SGD</div>
                              <input
                                type="text"
                                placeholder="0.00"
                                value={bulkBasePrice}
                                onChange={(e) => {
                                  if (!isValidPriceInput(e.target.value)) return;
                                  setBulkBasePrice(e.target.value);
                                  // Bulk base price fills every row, so base-price errors are no longer certain
                                  setBulkErrors((prev) => {
                                    const n = { ...prev };
                                    Object.keys(n).forEach((k) => { if (!k.endsWith("_value")) delete n[k]; });
                                    return n;
                                  });
                                }}
                                onBlur={(e) => setBulkBasePrice(roundPriceOnBlur(e.target.value))}
                                style={inputStyle}
                              />
                            </div>
                          </div>

                          {/* Bulk Platform Price */}
                          {!isEmpty && <div style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "nowrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap" }}>
                                Bulk Edit Platform Price
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 400, color: "#6C757D", fontFamily: "Montserrat", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                (This is a one-time bulk pricing tool. Price correlations between "Base Price" and "Platform Price" won't be saved. Only updating "Base Price" later will not auto-refresh "Platform Price".)
                              </span>
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: "#fff" }}>
                              <colgroup>
                                <col style={{ width: "25%" }} />
                                <col style={{ width: "37.5%" }} />
                                <col style={{ width: "37.5%" }} />
                              </colgroup>
                              <thead>
                                <tr>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Platform</th>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Bulk Pricing Type</th>
                                  <th style={{ fontSize: 12, fontWeight: 700, color: "#212529", fontFamily: "Montserrat", padding: "10px", textAlign: "left", background: "#E9ECEF" }}>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allPlatforms.map((pname) => {
                                  const selectedType = bulkPlatformTypes[pname] || "";
                                  const isDropOpen = openBulkTypeDropdown === pname;
                                  const isTemplateDropOpen = openTemplateDropdown === pname;
                                  const typeError = bulkErrors[pname];
                                  const valueError = bulkErrors[`${pname}_value`];
                                  const hasTypeError = !!typeError;
                                  const hasValueError = !!valueError;
                                  return (
                                    <tr key={pname}>
                                      <td style={{ padding: "10px", fontSize: 12, color: "#212529", fontFamily: "Montserrat", borderBottom: "1px solid #DEE2E6", height: 60 }}>{pname}</td>

                                      {/* Bulk Pricing Type dropdown */}
                                      <td style={{ padding: "10px", borderBottom: "1px solid #DEE2E6", height: 60 }}>
                                        <div
                                          ref={(el) => { bulkTypeDropdownRef.current[pname] = el; }}
                                          style={{ position: "relative", display: "block", width: "100%" }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => setOpenBulkTypeDropdown(isDropOpen ? null : pname)}
                                            style={{
                                              width: "100%", height: 40, display: "flex", alignItems: "center", justifyContent: "space-between",
                                              padding: "0 10px",
                                              border: `1px solid ${hasTypeError ? "#DC3545" : "#6C757D"}`,
                                              borderRadius: 8, background: "#fff",
                                              fontSize: 14, color: selectedType ? "#212529" : "#6C757D",
                                              fontFamily: "Montserrat", cursor: "pointer",
                                            }}
                                          >
                                            <span>{selectedType ? BULK_TYPE_DISPLAY[selectedType] : "Select..."}</span>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                              {selectedType && (
                                                <span
                                                  onClick={(e) => { e.stopPropagation(); clearBulkType(pname); }}
                                                  style={{ display: "flex", alignItems: "center", color: "#6C757D", cursor: "pointer" }}
                                                >
                                                  <X size={13} />
                                                </span>
                                              )}
                                              <ChevronDown size={14} color="#6C757D" />
                                            </div>
                                          </button>
                                          {hasTypeError && (
                                            <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{typeError}</div>
                                          )}
                                          {isDropOpen && (
                                            <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, background: "#fff", border: "1px solid #DEE2E6", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", width: "100%", marginTop: 4 }}>
                                              {BULK_TYPES.map((t) => (
                                                <div
                                                  key={t}
                                                  onClick={() => {
                                                    setBulkPlatformTypes((prev) => ({ ...prev, [pname]: t }));
                                                    setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                    setOpenBulkTypeDropdown(null);
                                                  }}
                                                  style={{ padding: "9px 14px", fontSize: 14, fontWeight: 400, fontFamily: "Montserrat", color: "#212529", cursor: "pointer", background: "#fff" }}
                                                  onMouseEnter={(e) => e.currentTarget.style.background = "#FFFEF0"}
                                                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                                                >
                                                  {t}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </td>

                                      {/* Value column */}
                                      <td style={{ padding: "10px", borderBottom: "1px solid #DEE2E6", height: 60 }}>
                                        {selectedType === "Bulk Input" ? (
                                          <div>
                                            <div style={{ ...inputWrapStyle(hasValueError), maxWidth: "none", width: "100%" }}>
                                              <div style={sgdStyle(hasValueError)}>SGD</div>
                                              <input
                                                type="text"
                                                placeholder="0.00"
                                                value={bulkPlatformValues[pname] || ""}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  if (!isValidPriceInput(v)) return;
                                                  setBulkPlatformValues((prev) => ({ ...prev, [pname]: v }));
                                                  if (bulkErrors[pname] || bulkErrors[`${pname}_value`]) setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                }}
                                                onBlur={(e) => {
                                                  const rounded = roundPriceOnBlur(e.target.value);
                                                  setBulkPlatformValues((prev) => ({ ...prev, [pname]: rounded }));
                                                }}
                                                style={{ ...inputStyle, color: hasValueError ? "#DC3545" : "#6C757D" }}
                                              />
                                            </div>
                                            {hasValueError && (
                                              <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{valueError}</div>
                                            )}
                                          </div>
                                        ) : selectedType === "Use Pricing Template" ? (
                                          <div
                                            ref={(el) => { templateDropdownRef.current[pname] = el; }}
                                            style={{ position: "relative", display: "block", width: "100%" }}
                                          >
                                            <div style={{ display: "flex", alignItems: "center", height: 40, border: `1px solid ${hasValueError ? "#DC3545" : "#6C757D"}`, borderRadius: 8, background: "#fff", padding: "0 10px", gap: 4 }}>
                                              <input
                                                type="text"
                                                placeholder="Search and Select..."
                                                value={isTemplateDropOpen ? (templateSearch[pname] || "") : (bulkPlatformTemplates[pname]?.name || "")}
                                                onFocus={() => {
                                                  setTemplateSearch((prev) => ({ ...prev, [pname]: "" }));
                                                  setOpenTemplateDropdown(pname);
                                                }}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  setTemplateSearch((prev) => ({ ...prev, [pname]: v }));
                                                  if (!isTemplateDropOpen) setOpenTemplateDropdown(pname);
                                                }}
                                                style={{ border: "none", outline: "none", flex: 1, fontSize: 14, fontFamily: "Montserrat", color: "#212529", background: "transparent", minWidth: 0 }}
                                              />
                                              {bulkPlatformTemplates[pname] && !isTemplateDropOpen && (
                                                <span
                                                  onMouseDown={(e) => { e.preventDefault(); setBulkPlatformTemplates((prev) => { const n = { ...prev }; delete n[pname]; return n; }); setOpenTemplateDropdown(null); }}
                                                  style={{ display: "flex", alignItems: "center", color: "#6C757D", cursor: "pointer", flexShrink: 0 }}
                                                >
                                                  <X size={13} />
                                                </span>
                                              )}
                                              <ChevronDown size={14} color="#6C757D" style={{ flexShrink: 0 }} />
                                            </div>
                                            {isTemplateDropOpen && (
                                              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, background: "#fff", border: "1px solid #DEE2E6", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", width: "100%", marginTop: 4, maxHeight: 220, overflowY: "auto" }}>
                                                {(() => {
                                                  const q = (templateSearch[pname] || "").toLowerCase();
                                                  const filtered = pricingTemplates.filter((t) => t.template_name.toLowerCase().includes(q));
                                                  if (!filtered.length) return (
                                                    <div style={{ padding: "10px 14px", fontSize: 12, color: "#ADB5BD", fontFamily: "Montserrat" }}>No templates found</div>
                                                  );
                                                  return filtered.map((t) => (
                                                    <div
                                                      key={t.pricing_template_id}
                                                      onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setBulkPlatformTemplates((prev) => ({ ...prev, [pname]: { id: t.pricing_template_id, name: t.template_name, fees: t.fees || [] } }));
                                                        setBulkErrors((prev) => { const n = { ...prev }; delete n[pname]; delete n[`${pname}_value`]; return n; });
                                                        setOpenTemplateDropdown(null);
                                                        setTemplateSearch((prev) => ({ ...prev, [pname]: "" }));
                                                      }}
                                                      style={{ padding: "9px 14px", fontSize: 13, fontFamily: "Montserrat", color: "#212529", cursor: "pointer", background: "#fff" }}
                                                      onMouseEnter={(e) => e.currentTarget.style.background = "#FFFEF0"}
                                                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                                                    >
                                                      {t.template_name}
                                                    </div>
                                                  ));
                                                })()}
                                              </div>
                                            )}
                                          {hasValueError && selectedType === "Use Pricing Template" && (
                                            <div style={{ color: "#DC3545", fontSize: 11, marginTop: 4, fontFamily: "Montserrat" }}>{valueError}</div>
                                          )}
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: 12, color: "#212529", fontFamily: "Montserrat" }}>-</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>}

                          {/* Panel footer */}
                          {(() => {
                            const hasBulkData = bulkBasePrice.trim() !== "" || Object.values(bulkPlatformTypes).some((t) => !!t);
                            return (
                              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                <button
                                  type="button"
                                  onClick={() => { setBulkEditOpen(false); setBulkBasePrice(""); setBulkPlatformTypes({}); setBulkPlatformValues({}); setBulkPlatformTemplates({}); setBulkErrors({}); setTemplateSearch({}); bulkAppliedRef.current = false; }}
                                  style={{ height: 34, minWidth: 100, padding: "0 10px", borderRadius: 8, border: "none", background: "#fff", fontSize: 12, fontFamily: "Montserrat", fontWeight: 600, cursor: "pointer", boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)", color: "#212529" }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={hasBulkData ? handleBulkApply : undefined}
                                  style={{
                                    borderRadius: 8,
                                    border: hasBulkData ? "none" : "1px solid #CED4DA",
                                    background: hasBulkData ? "#212529" : "#E9ECEF",
                                    color: hasBulkData ? "#fff" : "#6C757D",
                                    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
                                    display: "flex",
                                    height: 34,
                                    minWidth: 100,
                                    padding: "0 10px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 5,
                                    fontSize: 12,
                                    fontFamily: "Montserrat",
                                    fontWeight: 600,
                                    cursor: hasBulkData ? "pointer" : "not-allowed",
                                  }}
                                >
                                  Apply
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>}

                  {isEmpty ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 20px 30px" }}>
                      <img src={emptyBoxImg} alt="No listings" style={{ width: 120, height: 120, objectFit: "contain", marginBottom: 16 }} />
                      <h2 style={{ fontSize: 20, fontWeight: 600, color: "#212529", fontFamily: "Montserrat", marginBottom: 8 }}>No Listings Selected</h2>
                      <p style={{ fontSize: 14, color: "#6C757D", fontFamily: "Montserrat", lineHeight: "18px" }}>Back to select Listings and initiate bulk pricing</p>
                    </div>
                  ) : (
                    <div className="table-wrapper" style={{ overflowX: "auto", border: "none" }}>
                      <table className="entities-table tab-table w-100">
                        <thead>
                          <tr>
                            <th style={{ width: 40 }} />
                            <th style={{ minWidth: 200 }}>Stock</th>
                            <th style={{ minWidth: 120 }}>Cost Price</th>
                            <th style={{ minWidth: 200 }}>Base Price</th>
                            <th style={{ minWidth: 120 }}>Platform</th>
                            <th style={{ minWidth: 220 }}>Platform Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItems.map((item, idx) => {
                            const platforms = item.platforms || [];
                            const baseKey = `${item.id}_base`;
                            const basePriceVal = editedPrices[baseKey] ?? "";
                            return (
                              <tr key={item.id} style={{ background: idx % 2 === 1 ? "#F8F9FB" : "transparent" }}>
                                {/* Delete */}
                                <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRow(item.id)}
                                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                      <path d="M2.25 4.5H15.75M14.25 4.5V15C14.25 15.75 13.5 16.5 12.75 16.5H5.25C4.5 16.5 3.75 15.75 3.75 15V4.5M6 4.5V3C6 2.25 6.75 1.5 7.5 1.5H10.5C11.25 1.5 12 2.25 12 3V4.5M7.5 8.25V12.75M10.5 8.25V12.75" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                </td>

                                {/* Stock */}
                                <td style={{ verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <img src={item.avatar || noImage} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 400, color: "#212529", fontFamily: "Montserrat" }}>{item.name}</div>
                                      <div style={{ fontSize: 10, color: "#6C757D", marginTop: 2, fontFamily: "Montserrat" }}>{item.variant}{item.model_number ? ` | ${item.model_number}` : ""}</div>
                                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                                        <span style={{ border: "1px solid #FFEB00", backgroundColor: "#FFEB00", color: "#212529", fontSize: 8, fontWeight: 600, borderRadius: 99, padding: "2px 5px", fontFamily: "Montserrat" }}>{item.condition}</span>
                                        <span style={{ border: "1px solid #FFEB00", backgroundColor: "#FFEB00", color: "#212529", fontSize: 8, fontWeight: 600, borderRadius: 99, padding: "2px 5px", fontFamily: "Montserrat" }}>{item.grading}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Cost Price */}
                                <td style={{ verticalAlign: "middle", fontSize: 12, color: "#212529" }}>
                                  {item.costPriceData
                                    ? item.costPriceData.is_uniform
                                      ? `SGD ${Number(item.costPriceData.min).toFixed(2)}`
                                      : `SGD ${Number(item.costPriceData.min).toFixed(2)} → SGD ${Number(item.costPriceData.max).toFixed(2)}`
                                    : <span style={{ color: "#ADB5BD" }}>-</span>}
                                </td>

                                {/* Base Price */}
                                <td style={{ verticalAlign: "middle" }}>
                                  <div style={inputWrapStyle(false)}>
                                    <div style={sgdStyle(false)}>SGD</div>
                                    <input
                                      type="text"
                                      placeholder="0.00"
                                      value={basePriceVal}
                                      onChange={(e) => { const v = e.target.value; if (!isValidPriceInput(v)) return; setEditedPrices((prev) => ({ ...prev, [baseKey]: v })); }}
                                      onBlur={(e) => {
                                        const rounded = roundPriceOnBlur(e.target.value);
                                        setEditedPrices((prev) => ({ ...prev, [baseKey]: rounded }));
                                      }}
                                      style={inputStyle}
                                    />
                                  </div>
                                </td>

                                {/* Platform */}
                                <td style={{ verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {platforms.map((p) => (
                                      <div key={p.sales_listing_mapping_id} style={{ height: 40, display: "flex", alignItems: "center" }}>
                                        <span style={{ fontSize: 12, color: "#212529", fontFamily: "Montserrat" }}>{p.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>

                                {/* Platform Price */}
                                <td style={{ verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {platforms.map((p) => {
                                      const key = `${item.id}_${p.sales_listing_mapping_id}`;
                                      const val = editedPrices[key] ?? "";
                                      const hasErr = !!priceErrors[key];
                                      return (
                                        <div key={p.sales_listing_mapping_id}>
                                          <div style={inputWrapStyle(hasErr)}>
                                            <div style={sgdStyle(hasErr)}>SGD</div>
                                            <input
                                              type="text"
                                              placeholder="0.00"
                                              value={val}
                                              onChange={(e) => {
                                                const v = e.target.value;
                                                if (!isValidPriceInput(v)) return;
                                                setEditedPrices((prev) => ({ ...prev, [key]: v }));
                                                if (priceErrors[key]) setPriceErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
                                              }}
                                              onBlur={(e) => {
                                                const rounded = roundPriceOnBlur(e.target.value);
                                                setEditedPrices((prev) => ({ ...prev, [key]: rounded }));
                                              }}
                                              style={{ ...inputStyle, color: hasErr ? "#DC3545" : "#6C757D" }}
                                            />
                                          </div>
                                          {hasErr && (
                                            <div style={{ color: "#DC3545", fontSize: 11, marginTop: 2, fontFamily: "Montserrat" }}>
                                              {priceErrors[key]}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
